// Wizard de création de dossier TEF (CC-DEV1-31) — vrai stepper 4 étapes :
// Bénéficiaire → Logement → MaPrimeAdapt' → Documents.
// SOCLE générique (réutilise BanAddressInput partagé + ClientDuplicateDialog + endpoints
// dedup/clients/dossiers existants). Validation NON bloquante (on avance avec des champs
// vides ; seuls les champs vraiment obligatoires bloquent le bouton Suivant).
// Source unique : client → metier_clients (POST/PATCH), dossier → metier_dossiers.
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { ArrowLeft, ArrowRight, Save, Loader2, Check, Upload, User, Home, HeartHandshake, FileText, Sparkles } from "lucide-react";
import BanAddressInput from "../../components/shared/BanAddressInput";
import ClientDuplicateDialog from "./dossier/ClientDuplicateDialog";

const API = process.env.REACT_APP_BACKEND_URL || "";
const lbl = { color: "var(--oh-text-muted)" };
const inputStyle = { background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" };

function Txt({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="text-xs mb-1 block" style={lbl}>{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || "À remplir"}
        data-testid={testId} className="w-full rounded px-3 py-2 text-sm" style={inputStyle} />
    </div>
  );
}
function Sel({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs mb-1 block" style={lbl}>{label}</label>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full rounded px-3 py-2 text-sm" style={inputStyle}>
        <option value="">À remplir</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

const ALL_STEPS = [
  { key: "beneficiaire", label: "Bénéficiaire", icon: User },
  { key: "logement", label: "Logement", icon: Home },
  { key: "mpa", label: "MaPrimeAdapt'", icon: HeartHandshake },
  { key: "documents", label: "Documents", icon: FileText },
];

// profile="installateur" (CC-DEV1) : flux réduit dérivé du même socle. L'installateur
// saisit l'essentiel (bénéficiaire + adresse) + IA ; l'éligibilité MPA reste le
// métier de l'AMO (étape MPA masquée). Tout le reste (dédup, BAN, création) réutilisé.
export default function CreateDossierTefWizard({ tenantId, activite, civiliteOptions = [], onBack, onSuccess, profile = null }) {
  const STEPS = profile === "installateur" ? ALL_STEPS.filter((s) => s.key !== "mpa") : ALL_STEPS;
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // Bénéficiaire : nouveau OU existant
  const [clientMode, setClientMode] = useState("nouveau");   // "nouveau" | "existant"
  const [existingClient, setExistingClient] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [benef, setBenef] = useState({ civilite: "", nom: "", prenom: "", email: "", telephone: "", date_naissance: "" });
  const [logement, setLogement] = useState({ adresse: "", code_postal: "", ville: "", type_logement: "", surface_habitable: "", statut_occupation: "" });
  const [mpa, setMpa] = useState({ gir: "", rfr: "", nb_personnes_foyer: "", age: "", beneficiaire_apa: false, beneficiaire_pch: false });

  // Documents (pré-remplissage IA)
  const [extracting, setExtracting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);   // {champ: valeur} proposées
  const [docName, setDocName] = useState("");

  const [dupDialog, setDupDialog] = useState({ open: false, matches: [], onUse: null, onCreate: null });

  const headers = () => {
    const t = localStorage.getItem("session_token");
    return { "X-Tenant-ID": tenantId || "tef", "Content-Type": "application/json", ...(t && { Authorization: `Bearer ${t}` }) };
  };

  // ── Validation NON bloquante : seul l'obligatoire bloque le « Suivant ». ──
  // Indexée par CLÉ d'étape (robuste aux profils qui retirent des étapes).
  const stepRequiredMissing = (i) => {
    const key = STEPS[i]?.key;
    if (key === "beneficiaire") return clientMode === "existant" ? (existingClient ? [] : ["sélectionner un client"]) : (benef.nom.trim() ? [] : ["nom du bénéficiaire"]);
    return []; // Logement / MPA / Documents : tout est optionnel (complétable plus tard)
  };
  // Compteur « X champs à remplir » (optionnels vides de l'étape courante).
  const emptyCount = (i) => {
    const key = STEPS[i]?.key;
    const empty = (o, keys) => keys.filter((k) => !String(o[k] ?? "").trim()).length;
    if (key === "beneficiaire") return clientMode === "existant" ? 0 : empty(benef, ["civilite", "nom", "prenom", "email", "telephone", "date_naissance"]);
    if (key === "logement") return empty(logement, ["adresse", "code_postal", "ville", "type_logement", "surface_habitable", "statut_occupation"]);
    if (key === "mpa") return empty(mpa, ["gir", "rfr", "nb_personnes_foyer", "age"]);
    return 0;
  };

  const missing = stepRequiredMissing(step);
  const canNext = missing.length === 0;

  const doSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setResults([]); return; }
    try {
      const r = await fetch(`${API}/api/metier/clients/search?q=${encodeURIComponent(q)}&limit=5`, { headers: headers(), credentials: "include" });
      if (r.ok) setResults(await r.json());
    } catch { /* noop */ }
  };

  // Pré-remplissage IA : dépose un document → l'IA SUGGÈRE (n'écrit pas en dur).
  const onUploadDoc = async (file) => {
    if (!file) return;
    setDocName(file.name); setExtracting(true); setSuggestions(null);
    try {
      const t = localStorage.getItem("session_token");
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/api/metier/dossiers/extract-beneficiaire`, {
        method: "POST", credentials: "include",
        headers: { "X-Tenant-ID": tenantId || "tef", ...(t && { Authorization: `Bearer ${t}` }) },
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        const sugg = d.suggestions || {};
        setSuggestions(Object.keys(sugg).length ? sugg : {});
        toast.success(Object.keys(sugg).length ? `${Object.keys(sugg).length} suggestion(s) — à vérifier` : "Document déposé (aucune donnée extraite)");
      } else {
        toast.message("Document déposé — extraction IA indisponible, saisie manuelle.");
        setSuggestions({});
      }
    } catch { toast.error("Erreur réseau"); setSuggestions({}); }
    finally { setExtracting(false); }
  };
  // Application NON destructive : ne remplit QUE les champs vides.
  const applySuggestions = () => {
    if (!suggestions) return;
    const s = suggestions;
    setBenef((b) => ({ ...b,
      civilite: b.civilite || s.civilite || "", nom: b.nom || s.nom || "", prenom: b.prenom || s.prenom || "",
      date_naissance: b.date_naissance || s.date_naissance || "", email: b.email || s.email || "", telephone: b.telephone || s.telephone || "" }));
    setLogement((l) => ({ ...l, adresse: l.adresse || s.adresse || "", code_postal: l.code_postal || s.code_postal || "", ville: l.ville || s.ville || "" }));
    setMpa((m) => ({ ...m, rfr: m.rfr || s.rfr || "", nb_personnes_foyer: m.nb_personnes_foyer || s.nb_personnes_foyer || "" }));
    toast.success("Suggestions appliquées aux champs vides — vérifiez puis créez.");
  };

  const num = (v) => (v === "" || v == null ? null : (parseFloat(String(v).replace(",", ".")) || null));

  // Résout/crée le client puis crée le dossier. dedup sur création client (409) + dossier.
  const create = async (forceClient = false, forceDossier = false) => {
    setCreating(true); setError(null);
    try {
      let clientId = existingClient?.id;
      if (clientMode === "nouveau" && !clientId) {
        const body = {
          civilite: benef.civilite || "M.", nom: benef.nom, prenom: benef.prenom,
          email: benef.email, telephone: benef.telephone, date_naissance: benef.date_naissance,
          type_client: "particulier",
          adresse: { rue: logement.adresse || "", code_postal: logement.code_postal || "", ville: logement.ville || "" },
        };
        for (const k of Object.keys(body)) if (body[k] === "" || body[k] == null) delete body[k];
        const r = await fetch(`${API}/api/metier/clients${forceClient ? "?force=true" : ""}`, { method: "POST", headers: headers(), credentials: "include", body: JSON.stringify(body) });
        if (r.status === 409) {
          const err = await r.json().catch(() => ({}));
          const matches = (err?.detail?.candidates || []).map((c) => ({ id: c.client_id, nom: c.nom, prenom: c.prenom, email: c.email || "", adresse: { code_postal: c.code_postal, ville: c.ville }, reference: c.reference, score: c.score, match_reasons: c.match_reasons || [] }));
          setCreating(false);
          setDupDialog({ open: true, matches,
            onUse: async (m) => { setDupDialog({ open: false, matches: [] }); setExistingClient({ id: m.id, nom: m.nom, prenom: m.prenom }); setClientMode("existant"); },
            onCreate: () => { setDupDialog({ open: false, matches: [] }); create(true, forceDossier); } });
          return;
        }
        if (!r.ok) { const e = await r.json().catch(() => ({})); setError(e?.detail || "Erreur création client"); setCreating(false); return; }
        clientId = (await r.json()).id;
      }
      if (!clientId) { setError("Bénéficiaire requis"); setCreating(false); return; }

      // Doublon DOSSIER (client existant déjà engagé)
      if (!forceDossier) {
        try {
          const rd = await fetch(`${API}/api/metier/dossiers/check-duplicate-by-client?client_id=${encodeURIComponent(clientId)}`, { headers: headers(), credentials: "include" });
          const dd = rd.ok ? await rd.json() : { matches: [] };
          if ((dd.matches || []).length) {
            setCreating(false);
            setDupDialog({ open: true, mode: "dossier", matches: dd.matches,
              onUse: (m) => { setDupDialog({ open: false, matches: [] }); onSuccess?.({ id: m.id, reference: m.reference, activite }); },
              onCreate: () => { setDupDialog({ open: false, matches: [] }); create(forceClient, true); } });
            return;
          }
        } catch { /* noop */ }
      }

      // Création du dossier (source unique : adresse_projet + activité + client_id)
      const dossierBody = { activite, client_id: clientId,
        adresse_projet: { rue: logement.adresse || "", code_postal: logement.code_postal || "", ville: logement.ville || "" } };
      const rdos = await fetch(`${API}/api/metier/dossiers`, { method: "POST", headers: headers(), credentials: "include", body: JSON.stringify(dossierBody) });
      if (!rdos.ok) { const e = await rdos.json().catch(() => ({})); setError(e?.detail || "Erreur création dossier"); setCreating(false); return; }
      const dossier = await rdos.json();

      // PATCH des champs LOGEMENT + MPA → routés vers metier_clients (source unique).
      const patch = {
        date_naissance: benef.date_naissance || undefined,
        statut_occupation: logement.statut_occupation || undefined,
        surface_habitable: num(logement.surface_habitable),
        gir: mpa.gir || undefined, rfr: num(mpa.rfr), nb_personnes_foyer: num(mpa.nb_personnes_foyer),
        age: num(mpa.age), beneficiaire_apa: mpa.beneficiaire_apa || undefined, beneficiaire_pch: mpa.beneficiaire_pch || undefined,
        logement: { type_logement: logement.type_logement || undefined, surface_habitable: num(logement.surface_habitable), statut_occupation: logement.statut_occupation || undefined },
      };
      for (const k of Object.keys(patch)) if (patch[k] === undefined) delete patch[k];
      try {
        await fetch(`${API}/api/metier/dossiers/${dossier.id}`, { method: "PATCH", headers: headers(), credentials: "include", body: JSON.stringify(patch) });
      } catch { /* non bloquant : le dossier est créé */ }
      toast.success(`Dossier ${dossier.reference || ""} créé`);
      onSuccess?.(dossier);
    } catch (e) {
      setError("Erreur réseau"); setCreating(false);
    }
  };

  const isLast = step === STEPS.length - 1;
  const cur = STEPS[step]?.key;  // render par clé (profil-agnostique)

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="tef-wizard">
      {/* En-tête + stepper */}
      <div className="flex items-center gap-3">
        {onBack && <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>}
        <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Nouveau dossier {activite === "tef" ? "TEF (MaPrimeAdapt')" : ""}</h2>
      </div>
      <div className="flex items-center gap-2" data-testid="tef-wizard-stepper">
        {STEPS.map((s, i) => {
          const on = i === step, done = i < step;
          return (
            <div key={s.key} className="flex items-center gap-2 flex-1">
              <button type="button" onClick={() => i <= step && setStep(i)} data-testid={`tef-step-${s.key}`}
                className="flex items-center gap-2 rounded px-3 py-1.5 text-sm w-full"
                style={{ background: on ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-surface)", border: `1px solid ${on ? "var(--oh-stat-blue-border)" : "var(--oh-border)"}`, color: on ? "var(--oh-stat-blue-text)" : "var(--oh-text)", opacity: done ? 0.8 : 1 }}>
                {done ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                <span className="truncate">{i + 1}. {s.label}</span>
              </button>
            </div>
          );
        })}
      </div>
      <div className="text-xs" data-testid="tef-step-counter" style={lbl}>
        Étape {step + 1}/{STEPS.length}{emptyCount(step) > 0 ? ` — ${emptyCount(step)} champ(s) à remplir (complétez maintenant ou plus tard)` : " — complet"}
      </div>

      <div className="rounded-lg p-4" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
        {cur === "beneficiaire" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              {[["nouveau", "Nouveau bénéficiaire"], ["existant", "Bénéficiaire existant"]].map(([m, t]) => (
                <button key={m} type="button" onClick={() => setClientMode(m)} data-testid={`tef-client-${m}`}
                  className="rounded px-3 py-2 text-sm" style={{ background: clientMode === m ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-surface)", border: `1px solid ${clientMode === m ? "var(--oh-stat-blue-border)" : "var(--oh-border)"}`, color: clientMode === m ? "var(--oh-stat-blue-text)" : "var(--oh-text)" }}>{t}</button>
              ))}
            </div>
            {clientMode === "existant" ? (
              <div className="space-y-2">
                <Txt label="Rechercher un client (nom)" value={search} onChange={doSearch} placeholder="Taper un nom…" testId="tef-client-search" />
                {existingClient && <div className="text-sm rounded px-3 py-2" style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }}>Sélectionné : <b>{existingClient.nom} {existingClient.prenom}</b></div>}
                {results.map((c) => (
                  <button key={c.id} type="button" onClick={() => { setExistingClient(c); setResults([]); setSearch(`${c.nom} ${c.prenom || ""}`); }}
                    data-testid="tef-client-result" className="w-full text-left rounded px-3 py-2 text-sm" style={inputStyle}>
                    {c.nom} {c.prenom} {c.reference ? `· ${c.reference}` : ""}
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Sel label="Civilité" value={benef.civilite} onChange={(v) => setBenef({ ...benef, civilite: v })}
                  options={(civiliteOptions.length ? civiliteOptions.map((c) => ({ value: c.id, label: c.label })) : [{ value: "M.", label: "M." }, { value: "Mme", label: "Mme" }])} testId="tef-civilite" />
                <Txt label="Nom *" value={benef.nom} onChange={(v) => setBenef({ ...benef, nom: v })} testId="tef-nom" />
                <Txt label="Prénom" value={benef.prenom} onChange={(v) => setBenef({ ...benef, prenom: v })} testId="tef-prenom" />
                <Txt label="Date de naissance" type="date" value={benef.date_naissance} onChange={(v) => setBenef({ ...benef, date_naissance: v })} testId="tef-naissance" />
                <Txt label="Email" type="email" value={benef.email} onChange={(v) => setBenef({ ...benef, email: v })} testId="tef-email" />
                <Txt label="Téléphone" value={benef.telephone} onChange={(v) => setBenef({ ...benef, telephone: v })} testId="tef-tel" />
              </div>
            )}
          </div>
        )}

        {cur === "logement" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs mb-1 block" style={lbl}>Adresse du logement (autocomplétée)</label>
              <BanAddressInput value={logement.adresse} testId="tef-adresse"
                onChange={(v) => setLogement((l) => ({ ...l, adresse: v }))}
                onPick={({ rue, cp, ville }) => setLogement((l) => ({ ...l, adresse: rue, code_postal: cp || l.code_postal, ville: ville || l.ville }))} />
            </div>
            <Txt label="Code postal" value={logement.code_postal} onChange={(v) => setLogement({ ...logement, code_postal: v })} testId="tef-cp" />
            <Txt label="Ville" value={logement.ville} onChange={(v) => setLogement({ ...logement, ville: v })} testId="tef-ville" />
            <Sel label="Type de logement" value={logement.type_logement} onChange={(v) => setLogement({ ...logement, type_logement: v })}
              options={[{ value: "maison", label: "Maison" }, { value: "appartement", label: "Appartement" }]} testId="tef-type-logement" />
            <Txt label="Surface habitable (m²)" type="number" value={logement.surface_habitable} onChange={(v) => setLogement({ ...logement, surface_habitable: v })} testId="tef-surface" />
            <Sel label="Statut d'occupation" value={logement.statut_occupation} onChange={(v) => setLogement({ ...logement, statut_occupation: v })}
              options={[{ value: "proprietaire_occupant", label: "Propriétaire occupant" }, { value: "locataire", label: "Locataire" }, { value: "proprietaire_bailleur", label: "Propriétaire bailleur" }]} testId="tef-occupation" />
          </div>
        )}

        {cur === "mpa" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Sel label="GIR (autonomie)" value={mpa.gir} onChange={(v) => setMpa({ ...mpa, gir: v })}
              options={[1, 2, 3, 4, 5, 6].map((g) => ({ value: String(g), label: `GIR ${g}` }))} testId="tef-gir" />
            <Txt label="Âge" type="number" value={mpa.age} onChange={(v) => setMpa({ ...mpa, age: v })} testId="tef-age" />
            <Txt label="Revenu fiscal de référence (RFR)" type="number" value={mpa.rfr} onChange={(v) => setMpa({ ...mpa, rfr: v })} testId="tef-rfr" />
            <Txt label="Nombre de personnes du foyer" type="number" value={mpa.nb_personnes_foyer} onChange={(v) => setMpa({ ...mpa, nb_personnes_foyer: v })} testId="tef-foyer" />
            <label className="flex items-center gap-2 text-sm pt-6" style={{ color: "var(--oh-text)" }}>
              <input type="checkbox" checked={mpa.beneficiaire_apa} onChange={(e) => setMpa({ ...mpa, beneficiaire_apa: e.target.checked })} data-testid="tef-apa" /> Bénéficiaire APA
            </label>
            <label className="flex items-center gap-2 text-sm pt-6" style={{ color: "var(--oh-text)" }}>
              <input type="checkbox" checked={mpa.beneficiaire_pch} onChange={(e) => setMpa({ ...mpa, beneficiaire_pch: e.target.checked })} data-testid="tef-pch" /> Bénéficiaire PCH
            </label>
          </div>
        )}

        {cur === "documents" && (
          <div className="space-y-3">
            <div className="rounded p-3" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
              <div className="flex items-center gap-2 mb-2" style={{ color: "var(--oh-text)" }}>
                <Sparkles className="w-4 h-4" /> <b className="text-sm">Déposer un document et remplir (IA)</b>
              </div>
              <p className="text-xs mb-2" style={lbl}>CNI → identité · avis d'imposition → RFR. L'IA SUGGÈRE, vous validez (rien n'est écrit sans votre accord).</p>
              <label className="inline-flex items-center gap-2 rounded px-3 py-2 text-sm cursor-pointer" style={inputStyle} data-testid="tef-upload-label">
                <Upload className="w-4 h-4" /> {docName || "Choisir un document (PDF/JPG/PNG)"}
                <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" data-testid="tef-upload-input"
                  onChange={(e) => onUploadDoc(e.target.files?.[0])} />
              </label>
              {extracting && <div className="text-xs mt-2" style={lbl}><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Extraction en cours…</div>}
              {suggestions && Object.keys(suggestions).length > 0 && (
                <div className="mt-3" data-testid="tef-suggestions">
                  <div className="text-xs mb-1" style={lbl}>Suggestions IA (à vérifier) :</div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {Object.entries(suggestions).map(([k, v]) => (
                      <span key={k} className="text-xs rounded px-2 py-0.5" style={{ background: "var(--oh-stat-purple-bg)", color: "var(--oh-stat-purple-text)" }}>{k}: {String(v)}</span>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" onClick={applySuggestions} data-testid="tef-apply-suggestions">Appliquer aux champs vides</Button>
                </div>
              )}
            </div>
            {/* Récapitulatif */}
            <div className="text-sm rounded p-3" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}>
              <div className="font-medium mb-1">Récapitulatif</div>
              <div style={lbl} className="text-xs">Bénéficiaire : {existingClient ? `${existingClient.nom} ${existingClient.prenom || ""}` : `${benef.civilite} ${benef.nom} ${benef.prenom}`}</div>
              <div style={lbl} className="text-xs">Logement : {[logement.adresse, logement.code_postal, logement.ville].filter(Boolean).join(", ") || "—"}{logement.surface_habitable ? ` · ${logement.surface_habitable} m²` : ""}</div>
              <div style={lbl} className="text-xs">MPA : {mpa.gir ? `GIR ${mpa.gir}` : "GIR —"}{mpa.rfr ? ` · RFR ${mpa.rfr}` : ""}{mpa.nb_personnes_foyer ? ` · ${mpa.nb_personnes_foyer} pers.` : ""}</div>
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-sm rounded px-3 py-2" style={{ background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)" }}>{error}</div>}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}><ArrowLeft className="w-4 h-4 mr-1" />Précédent</Button>
        <div className="flex flex-col items-end gap-1">
          {!canNext && <span className="text-xs" data-testid="tef-blocked-msg" style={{ color: "var(--oh-stat-amber-text)" }}>Obligatoire : {missing.join(", ")}</span>}
          {!isLast ? (
            <Button onClick={() => canNext && setStep((s) => Math.min(STEPS.length - 1, s + 1))} disabled={!canNext} data-testid="tef-next">Suivant<ArrowRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button onClick={() => create()} disabled={creating || !canNext} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tef-create">
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Créer le dossier
            </Button>
          )}
        </div>
      </div>

      <ClientDuplicateDialog
        open={dupDialog.open} mode={dupDialog.mode || "client"} matches={dupDialog.matches}
        onUseExisting={dupDialog.onUse} onCreateAnyway={dupDialog.onCreate} onCancel={() => setDupDialog({ open: false, matches: [] })} />
    </div>
  );
}
